import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { usePlivo } from "@/lib/plivo-context";
import { getExtensionCallDisplay } from "@/lib/extension-call-display";
import { useDialpadState } from "@/lib/dialpad-state";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { apiRequest } from "@/lib/queryClient";
import type { Recording, UserSettings, PhoneNumber } from "@shared/schema";
import type { CsatTemplateOption } from "@shared/csat-template-options";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  Mic,
  MicOff,
  CirclePause,
  CirclePlay,
  Pause,
  Delete,
  X,
  AlertTriangle,
  Minimize2,
  Maximize2,
  User,
  Users,
  ExternalLink,
  PhoneForwarded,
  ArrowLeft,
  Loader2,
  GripHorizontal,
  ChevronDown,
  Search,
  Check,
  CheckCircle,
  Settings,
  Volume2,
  Voicemail,
  Bell,
  RefreshCw,
  Save,
  SmilePlus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  countries,
  detectCountryFromInput,
  buildDialNumber,
  getDefaultCountry,
  getCountryByDialCode,
  type Country,
} from "@/lib/countries";

type EnrichedPhoneNumber = PhoneNumber & { outboundAgentUserIds: string[] };

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function DialpadHeaderButton() {
  const { toggle, isOpen } = useDialpadState();
  const { callState, isRegistered } = usePlivo();
  const isInCall = callState === "in-progress" || callState === "ringing";
  const isIncoming = callState === "incoming";

  return (
    <div className="relative">
      <Button
        size="icon"
        variant={isInCall || isIncoming ? "default" : "ghost"}
        className={
          isInCall
            ? "bg-emerald-600 text-white"
            : isIncoming
              ? "bg-emerald-600 text-white animate-pulse"
              : "bg-emerald-600 text-white hover:bg-emerald-700"
        }
        onClick={toggle}
        data-testid="button-toggle-dialpad"
      >
        <Phone className="w-4 h-4" />
      </Button>
      {isIncoming && !isOpen && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
          <span className="text-[9px] text-white font-bold">1</span>
        </span>
      )}
    </div>
  );
}

export function FloatingDialpad() {
  const { user } = useAuth();
  const csatTemplateTenantId = user?.impersonating?.tenantId || user?.tenantId;
  const {
    isRegistered,
    callState,
    callerName,
    callDuration,
    isMuted,
    isOnHold,
    activeContactId,
    makeCall,
    answerCall,
    hangupCall,
    rejectCall,
    toggleMute,
    toggleHold,
    isRecordingPaused,
    pauseRecording,
    resumeRecording,
    sendDtmf,
    transferCall,
    mergeTransfer,
    completeTransfer,
    cancelTransfer,
    isTransferring,
    transferError,
    warmTransferPhase,
    warmTransferTarget,
    isDispositionBlocked,
    dncWarning,
    clearDncWarning,
    plivoRef,
    currentCallUuid,
    callDirection,
    transferToSurvey,
    activeCustomerNumber,
    ourNumber,
    incomingInternalCall,
    connectedEndpointId,
    isCallbackActiveCall,
    incomingQueueName,
    incomingIvrMenuName,
    incomingCampaignName,
    micBlockedDialogOpen,
    setMicBlockedDialogOpen,
    micBlockedMode,
    lastCallEndReason,
    clearCallEndReason,
  } = usePlivo();

  const { isOpen, isMinimized, open, close, minimize, restore } =
    useDialpadState();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [inputValue, setInputValue] = useState("");

  const { data: vmDropRecordings } = useQuery<Recording[]>({
    queryKey: ["/api/recordings"],
    select: (data: Recording[]) => data.filter(r => r.category === "voicemail_drop"),
  });

  const { data: userSettings } = useQuery<UserSettings>({
    queryKey: ["/api/user-settings"],
  });

  const [showVoicemailDrop, setShowVoicemailDrop] = useState(false);
  const [selectedVmDropRecording, setSelectedVmDropRecording] = useState<string>("");
  const [vmDropSearch, setVmDropSearch] = useState("");
  const [showCsatPickerDialog, setShowCsatPickerDialog] = useState(false);
  const [csatPickerTemplateId, setCsatPickerTemplateId] = useState("__auto__");
  const [csatTransferring, setCsatTransferring] = useState(false);

  const voicemailDropMutation = useMutation({
    mutationFn: async ({ callUuid, recordingId }: { callUuid: string; recordingId: string }) => {
      await apiRequest("POST", "/api/calls/voicemail-drop", { callUuid, recordingId });
    },
    onSuccess: () => {
      setShowVoicemailDrop(false);
      setSelectedVmDropRecording("");
      setVmDropSearch("");
      toast({ title: "Voicemail dropped successfully" });
    },
    onError: (err: any) => {
      toast({ title: "Voicemail drop failed", description: err.message || "Please try again", variant: "destructive" });
    },
  });

  const [selectedCountry, setSelectedCountry] =
    useState<Country>(getDefaultCountry());
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [defaultCountryLoaded, setDefaultCountryLoaded] = useState(false);

  const { data: tenantSettings } = useQuery<{ defaultCountryCode?: string; tenantBusinessSettings?: any }>({
    queryKey: ["/api/tenant-settings"],
  });

  const { data: tenantFeatures } = useQuery<Record<string, any>>({
    queryKey: ["/api/tenant/features"],
  });

  const customCallerIdsFeatureEnabled = tenantFeatures?.customCallerId === true;

  const { data: customCallerIdsList } = useQuery<{ id: string; name: string; number: string }[]>({
    queryKey: ["/api/tenant/custom-caller-ids"],
    enabled: customCallerIdsFeatureEnabled,
  });

  const stopRecordingButtonEnabled = (tenantFeatures?.stopRecordingButton) === true;

  const csatOutboundEnabled = !!(tenantSettings?.tenantBusinessSettings as any)?.csatOutboundEnabled;
  const csatCallDirectionEligible = callDirection === "inbound" || csatOutboundEnabled;

  const {
    data: csatTemplatesData,
    isLoading: csatTemplatesLoading,
    isError: csatTemplatesError,
  } = useQuery<CsatTemplateOption[]>({
    queryKey: ["/api/csat/template-options", csatTemplateTenantId],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/csat/template-options");
      return response.json() as Promise<CsatTemplateOption[]>;
    },
    enabled: !!(
      csatTemplateTenantId &&
      tenantSettings?.tenantBusinessSettings?.csatEnabled &&
      csatCallDirectionEligible
    ),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const csatTemplates = csatTemplatesData ?? [];

  useEffect(() => {
    if (tenantSettings?.defaultCountryCode && !defaultCountryLoaded) {
      const country = getCountryByDialCode(tenantSettings.defaultCountryCode);
      if (country) {
        setSelectedCountry(country);
      }
      setDefaultCountryLoaded(true);
    }
  }, [tenantSettings, defaultCountryLoaded]);

  useEffect(() => {
    clearDncWarning();
  }, [inputValue, clearDncWarning]);

  // Auto-open the dialpad whenever a call becomes active — covers auto-answer,
  // where the manual "Answer" button's open() call is never reached.
  // NOTE: do NOT include "incoming" here — the centered incoming-call popup
  // renders when (isIncoming && !isOpen); opening the dialpad immediately would
  // hide that popup behind the dialpad panel.
  useEffect(() => {
    if (callState === "in-progress") {
      open();
    }
  }, [callState, open]);

  // Close any open popovers when the dialpad panel closes, so they don't
  // get unmounted mid-flight and cause a ResizeObserver runtime error.
  useEffect(() => {
    if (!isOpen) {
      setCallerIdOpen(false);
      setCallerIdSearch("");
      setTransferDropdownOpen(false);
      setTransferSearch("");
      setCountryOpen(false);
      setCountrySearch("");
      setExtensionDropdownOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (inputValue.startsWith("+")) {
      const detected = detectCountryFromInput(inputValue);
      if (detected) {
        setSelectedCountry(detected.country);
      }
    }
  }, [inputValue]);

  const CALL_END_NOTIFICATIONS: Record<string, { type: "red" | "yellow"; message: string }> = {
    UNALLOCATED_NUMBER:        { type: "red",    message: "Number is out of service or doesn't exist" },
    NO_ROUTE_DESTINATION:      { type: "red",    message: "Number can't be reached — routing failed" },
    NO_ROUTE_TRANSIT_NET:      { type: "red",    message: "No carrier path available to this number" },
    INCOMPATIBLE_DESTINATION:  { type: "red",    message: "Destination rejected the call" },
    INVALID_NUMBER_FORMAT:     { type: "red",    message: "Number format is invalid" },
    NUMBER_CHANGED:            { type: "red",    message: "This number has been changed or disconnected" },
    SUBSCRIBER_ABSENT:         { type: "red",    message: "Number temporarily unreachable" },
    OUT_OF_SERVICE:            { type: "red",    message: "Number is out of service" },
    DESTINATION_OUT_OF_ORDER:  { type: "red",    message: "Destination is out of order" },
    NETWORK_OUT_OF_ORDER:      { type: "red",    message: "Network error — try again" },
    EXCHANGE_ROUTING_ERROR:    { type: "red",    message: "Carrier routing error" },
    RECOVERY_ON_TIMER_EXPIRE:  { type: "red",    message: "Call timed out — no answer" },
    NO_ANSWER:                 { type: "yellow", message: "No answer" },
    USER_BUSY:                 { type: "yellow", message: "Line is busy" },
    CALL_REJECTED:             { type: "yellow", message: "Call was declined" },
    LOSE_RACE:                 { type: "yellow", message: "Call couldn't connect — try again" },
    NORMAL_UNSPECIFIED:        { type: "red",    message: "Call failed — carrier error" },
  };
  const callEndNotification = lastCallEndReason
    ? (CALL_END_NOTIFICATIONS[lastCallEndReason] ?? {
        type: "red" as const,
        // Super admins see the raw Plivo cause; everyone else sees a generic message.
        message: user?.role === "super_admin" ? lastCallEndReason : "Call failed",
      })
    : null;

  const callEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (callEndTimerRef.current) clearTimeout(callEndTimerRef.current);
    if (lastCallEndReason) {
      callEndTimerRef.current = setTimeout(() => {
        clearCallEndReason();
        callEndTimerRef.current = null;
      }, 6000);
    }
    return () => {
      if (callEndTimerRef.current) clearTimeout(callEndTimerRef.current);
    };
  }, [lastCallEndReason]);

  useEffect(() => {
    if (inputValue && lastCallEndReason) clearCallEndReason();
  }, [inputValue]);

  const [dialTab, setDialTab] = useState<"phone" | "extension">("phone");
  const [selectedExtension, setSelectedExtension] = useState("");
  const [extensionDropdownOpen, setExtensionDropdownOpen] = useState(false);
  const [extensionSearch, setExtensionSearch] = useState("");
  const [showTransfer, setShowTransfer] = useState(false);

  // Reset local transfer panel when a call ends or a new one begins —
  // prevents the warm-transfer panel from carrying over to the next call.
  useEffect(() => {
    if (callState === "idle" || callState === "incoming") {
      setShowTransfer(false);
    }
  }, [callState]);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferType, setTransferType] = useState<"cold" | "warm">("cold");
  const [transferDropdownOpen, setTransferDropdownOpen] = useState(false);
  const [transferSearch, setTransferSearch] = useState("");
  const [selectedCallerId, setSelectedCallerId] = useState<string>("auto");
  const [callerIdSearch, setCallerIdSearch] = useState("");

  useEffect(() => {
    if (userSettings?.defaultCallerId && selectedCallerId === "auto") {
      setSelectedCallerId(userSettings.defaultCallerId);
    }
  }, [userSettings?.defaultCallerId]);
  const [callerIdOpen, setCallerIdOpen] = useState(false);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [audioDevices, setAudioDevices] = useState<{
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  }>({ microphones: [], speakers: [] });
  const [selectedMic, setSelectedMic] = useState<string>(
    () => localStorage.getItem("dialpad_mic") || "default",
  );
  const [selectedRingtone, setSelectedRingtone] = useState<string>(
    () => localStorage.getItem("dialpad_ringtone_speaker") || "default",
  );
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>(
    () => localStorage.getItem("dialpad_speaker") || "default",
  );
  const [refreshingDevices, setRefreshingDevices] = useState(false);
  const [testingRingtone, setTestingRingtone] = useState(false);
  const [testingSpeaker, setTestingSpeaker] = useState(false);
  const [testingMic, setTestingMic] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micAnimRef = useRef<number | null>(null);
  const micAudioCtxRef = useRef<AudioContext | null>(null);

  const playTestRingtone = useCallback(
    async (type: "ringtone" | "speaker") => {
      try {
        if (type === "ringtone") setTestingRingtone(true);
        else setTestingSpeaker(true);

        let played = false;
        if (plivoRef.current?.client?.audio) {
          try {
            // Use speakerDevices for the speaker test and ringtoneDevices for
            // the ringtone test — they may be routed to different physical devices.
            const deviceManager = type === "speaker"
              ? plivoRef.current.client.audio.speakerDevices
              : plivoRef.current.client.audio.ringtoneDevices;
            const testMedia = deviceManager.media();
            if (testMedia) {
              testMedia.currentTime = 0;
              await testMedia.play();
              played = true;
              setTimeout(() => {
                testMedia.pause();
                testMedia.currentTime = 0;
                if (type === "ringtone") setTestingRingtone(false);
                else setTestingSpeaker(false);
              }, 3000);
              return;
            }
          } catch (e) {
            console.warn(
              "[Audio] Plivo device media not available, using fallback:",
              e,
            );
          }
        }

        if (!played) {
          const audioCtx = new AudioContext();
          const gainNode = audioCtx.createGain();
          gainNode.connect(audioCtx.destination);
          gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
          const ringPattern = [
            { freq: 440, start: 0, end: 0.4 },
            { freq: 480, start: 0.05, end: 0.4 },
            { freq: 440, start: 0.6, end: 1.0 },
            { freq: 480, start: 0.65, end: 1.0 },
            { freq: 440, start: 1.2, end: 1.6 },
            { freq: 480, start: 1.25, end: 1.6 },
          ];
          for (const note of ringPattern) {
            const osc = audioCtx.createOscillator();
            osc.type = "sine";
            osc.frequency.setValueAtTime(
              note.freq,
              audioCtx.currentTime + note.start,
            );
            osc.connect(gainNode);
            osc.start(audioCtx.currentTime + note.start);
            osc.stop(audioCtx.currentTime + note.end);
          }
          gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 1.8);
          setTimeout(() => {
            if (type === "ringtone") setTestingRingtone(false);
            else setTestingSpeaker(false);
            audioCtx.close();
          }, 2000);
        }
      } catch (err) {
        console.error("[Audio] Test sound failed:", err);
        if (type === "ringtone") setTestingRingtone(false);
        else setTestingSpeaker(false);
      }
    },
    [plivoRef],
  );

  const startMicTest = useCallback(async (deviceId: string) => {
    try {
      setTestingMic(true);
      const constraints: MediaStreamConstraints = {
        audio:
          deviceId !== "default" ? { deviceId: { exact: deviceId } } : true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      micStreamRef.current = stream;
      const audioCtx = new AudioContext();
      micAudioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const delayNode = audioCtx.createDelay(0.1);
      delayNode.delayTime.value = 0.05;
      const playbackGain = audioCtx.createGain();
      playbackGain.gain.value = 0.8;
      source.connect(delayNode);
      delayNode.connect(playbackGain);
      playbackGain.connect(audioCtx.destination);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        if (!micStreamRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length;
        setMicLevel(Math.min(100, Math.round((avg / 128) * 100)));
        micAnimRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (err) {
      console.error("[Audio] Mic test failed:", err);
      setTestingMic(false);
    }
  }, []);

  const stopMicTest = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (micAnimRef.current) {
      cancelAnimationFrame(micAnimRef.current);
      micAnimRef.current = null;
    }
    if (micAudioCtxRef.current) {
      micAudioCtxRef.current.close();
      micAudioCtxRef.current = null;
    }
    setTestingMic(false);
    setMicLevel(0);
  }, []);

  const toggleMicTest = useCallback(() => {
    if (testingMic) {
      stopMicTest();
    } else {
      startMicTest(selectedMic);
    }
  }, [testingMic, selectedMic, startMicTest, stopMicTest]);

  useEffect(() => {
    return () => {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (micAnimRef.current) {
        cancelAnimationFrame(micAnimRef.current);
      }
    };
  }, []);

  const loadAudioDevices = useCallback(
    async (autoSetup = false) => {
      try {
        setRefreshingDevices(true);
        await navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((s) => s.getTracks().forEach((t) => t.stop()));
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter((d) => d.kind === "audioinput");
        const speakers = devices.filter((d) => d.kind === "audiooutput");
        setAudioDevices({ microphones, speakers });

        if (autoSetup) {
          const savedMic = localStorage.getItem("dialpad_mic");
          const savedRing = localStorage.getItem("dialpad_ringtone_speaker");
          const savedSpk = localStorage.getItem("dialpad_speaker");

          const micToUse =
            microphones.find((d) => d.deviceId === savedMic) || microphones[0];
          const ringToUse =
            speakers.find((d) => d.deviceId === savedRing) || speakers[0];
          const spkToUse =
            speakers.find((d) => d.deviceId === savedSpk) || speakers[0];

          if (micToUse) {
            const id = micToUse.deviceId || "default";
            setSelectedMic(id);
            localStorage.setItem("dialpad_mic", id);
            if (plivoRef.current?.client?.audio) {
              try {
                plivoRef.current.client.audio.microphoneDevices.set(id);
              } catch (e) {}
            }
          }
          if (ringToUse) {
            const id = ringToUse.deviceId || "default";
            setSelectedRingtone(id);
            localStorage.setItem("dialpad_ringtone_speaker", id);
            if (plivoRef.current?.client?.audio) {
              try {
                plivoRef.current.client.audio.ringtoneDevices.set(id);
              } catch (e) {}
            }
          }
          if (spkToUse) {
            const id = spkToUse.deviceId || "default";
            setSelectedSpeaker(id);
            localStorage.setItem("dialpad_speaker", id);
            if (plivoRef.current?.client?.audio) {
              try {
                plivoRef.current.client.audio.speakerDevices.set(id);
              } catch (e) {}
            }
          }
        }
      } catch (err) {
        console.error("[Audio] Failed to enumerate devices:", err);
      } finally {
        setRefreshingDevices(false);
      }
    },
    [plivoRef],
  );

  useEffect(() => {
    if (showAudioSettings && audioDevices.microphones.length === 0) {
      loadAudioDevices();
    }
  }, [showAudioSettings, audioDevices.microphones.length, loadAudioDevices]);

  useEffect(() => {
    if (plivoRef.current?.client?.audio) {
      const audio = plivoRef.current.client.audio;
      const savedMic = localStorage.getItem("dialpad_mic");
      const savedRingtone = localStorage.getItem("dialpad_ringtone_speaker");
      const savedSpeaker = localStorage.getItem("dialpad_speaker");

      // Validate saved device IDs against the currently available devices before
      // applying them to the Plivo SDK. A stale ID (e.g. an unplugged Bluetooth
      // headset) will be silently ignored by the SDK, leaving the agent on the
      // wrong or nonexistent device. Clear the stale ID and let the SDK fall back
      // to its default so audio is at least functional on every page load.
      const applyDevice = (
        savedId: string | null,
        validDevices: MediaDeviceInfo[],
        setFn: (id: string) => void,
        storageKey: string,
        label: string,
      ) => {
        if (!savedId || savedId === "default") return;
        const exists = validDevices.some((d) => d.deviceId === savedId);
        if (!exists) {
          console.warn(`[Audio] Saved ${label} device (${savedId}) is no longer available — clearing saved ID`);
          localStorage.removeItem(storageKey);
          return;
        }
        try {
          setFn(savedId);
        } catch (e) {
          console.warn(`[Audio] Failed to set ${label} device:`, e);
        }
      };

      // Use already-enumerated device list if available; otherwise enumerate fresh.
      const allDevices = [...audioDevices.microphones, ...audioDevices.speakers];
      const applyAll = (devices: MediaDeviceInfo[]) => {
        const mics = devices.filter((d) => d.kind === "audioinput");
        const spks = devices.filter((d) => d.kind === "audiooutput");
        applyDevice(savedMic, mics, (id) => audio.microphoneDevices.set(id), "dialpad_mic", "microphone");
        applyDevice(savedRingtone, spks, (id) => audio.ringtoneDevices.set(id), "dialpad_ringtone_speaker", "ringtone speaker");
        applyDevice(savedSpeaker, spks, (id) => audio.speakerDevices.set(id), "dialpad_speaker", "speaker");
      };

      if (allDevices.length > 0) {
        applyAll(allDevices);
      } else {
        navigator.mediaDevices?.enumerateDevices().then(applyAll).catch(() => {
          // enumerateDevices unavailable — apply as-is with silent error handling
          if (savedMic && savedMic !== "default") try { audio.microphoneDevices.set(savedMic); } catch (_) {}
          if (savedRingtone && savedRingtone !== "default") try { audio.ringtoneDevices.set(savedRingtone); } catch (_) {}
          if (savedSpeaker && savedSpeaker !== "default") try { audio.speakerDevices.set(savedSpeaker); } catch (_) {}
        });
      }
    }
  }, [plivoRef.current?.client?.audio]);

  const applyMicDevice = useCallback(
    (deviceId: string) => {
      setSelectedMic(deviceId);
      localStorage.setItem("dialpad_mic", deviceId);
      if (plivoRef.current?.client?.audio) {
        plivoRef.current.client.audio.microphoneDevices.set(deviceId);
      }
      if (testingMic) {
        stopMicTest();
        setTimeout(() => startMicTest(deviceId), 100);
      }
    },
    [plivoRef, testingMic, stopMicTest, startMicTest],
  );

  const applyRingtoneDevice = useCallback(
    (deviceId: string) => {
      setSelectedRingtone(deviceId);
      localStorage.setItem("dialpad_ringtone_speaker", deviceId);
      if (plivoRef.current?.client?.audio) {
        plivoRef.current.client.audio.ringtoneDevices.set(deviceId);
      }
    },
    [plivoRef],
  );

  const applySpeakerDevice = useCallback(
    (deviceId: string) => {
      setSelectedSpeaker(deviceId);
      localStorage.setItem("dialpad_speaker", deviceId);
      if (plivoRef.current?.client?.audio) {
        plivoRef.current.client.audio.speakerDevices.set(deviceId);
      }
    },
    [plivoRef],
  );

  const saveAllSettings = useCallback(() => {
    localStorage.setItem("dialpad_mic", selectedMic);
    localStorage.setItem("dialpad_ringtone_speaker", selectedRingtone);
    localStorage.setItem("dialpad_speaker", selectedSpeaker);
    if (plivoRef.current?.client?.audio) {
      const audio = plivoRef.current.client.audio;
      if (selectedMic !== "default")
        try {
          audio.microphoneDevices.set(selectedMic);
        } catch (e) {}
      if (selectedRingtone !== "default")
        try {
          audio.ringtoneDevices.set(selectedRingtone);
        } catch (e) {}
      if (selectedSpeaker !== "default")
        try {
          audio.speakerDevices.set(selectedSpeaker);
        } catch (e) {}
    }
    setSettingsSaved(true);
    if (testingMic) {
      stopMicTest();
    }
    setTimeout(() => {
      setSettingsSaved(false);
      setShowAudioSettings(false);
    }, 1000);
  }, [selectedMic, selectedRingtone, selectedSpeaker, plivoRef, testingMic, stopMicTest]);

  const navigatedRef = useRef(false);
  const dragConstraintsRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const lower = countrySearch.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.dialCode.includes(lower) ||
        c.code.toLowerCase().includes(lower),
    );
  }, [countrySearch]);

  const { data: phoneNumbersList } = useQuery<EnrichedPhoneNumber[]>({
    queryKey: ["/api/phone-numbers"],
  });

  const activePhoneNumbers = (phoneNumbersList?.filter((pn) => pn.active && pn.voiceEnabled) || []).filter((pn) => {
    const ids = pn.outboundAgentUserIds || [];
    return ids.length > 0 && !!user?.id && ids.includes(user.id);
  });

  const callerIdLabel = (() => {
    if (selectedCallerId === "auto") return "Select Outbound Caller ID (Default)";
    if (selectedCallerId.startsWith("custom:")) {
      const customNumber = selectedCallerId.slice(7);
      const customEntry = (customCallerIdsList || []).find((c) => c.number === customNumber);
      if (customEntry) return `${customEntry.number} (${customEntry.name})`;
      return customNumber;
    }
    const pn = activePhoneNumbers.find((p) => p.id === selectedCallerId);
    if (!pn) return "Select Outbound Caller ID";
    return pn.friendlyName ? `${pn.number} (${pn.friendlyName})` : pn.number;
  })();

  const ourPhoneInfo = (() => {
    if (!ourNumber) return { number: "", friendlyName: "" };
    const normalized = ourNumber.replace(/^\+/, "");
    const pn = activePhoneNumbers.find(
      (p) => (p.number || "").replace(/^\+/, "") === normalized
    );
    if (pn) return { number: ourNumber, friendlyName: pn.friendlyName || "" };
    // Fall back to custom caller ID name if the number isn't a Plivo-provisioned number
    const customEntry = (customCallerIdsList || []).find(
      (c) => (c.number || "").replace(/^\+/, "") === normalized
    );
    return { number: ourNumber, friendlyName: customEntry?.name || "" };
  })();

  const ourNumberDisplay = ourPhoneInfo.friendlyName
    ? `${ourPhoneInfo.number} (${ourPhoneInfo.friendlyName})`
    : ourPhoneInfo.number;

  // For callback return calls the agent technically receives an inbound SIP call from the
  // system, but we track direction as "outbound" so ACW/logging work correctly.
  // For the dialpad display we want to show the customer's number at the top (fromDisplay)
  // just like a regular inbound call, so we treat callback calls the same as inbound here.
  const isDisplayInbound = callDirection === "inbound" || isCallbackActiveCall;
  const { data: extensionsList } = useQuery<any[]>({
    queryKey: ["/api/extensions"],
  });

  const { data: plivoEndpoints } = useQuery<any[]>({
    queryKey: ["/api/plivo-endpoints"],
  });

  const extensionDisplay = getExtensionCallDisplay({
    direction: callDirection,
    callback: isCallbackActiveCall,
    destination: activeCustomerNumber,
    connectedEndpointId,
    localName: user?.displayName,
    incoming: incomingInternalCall,
    extensions: extensionsList || [],
    endpoints: plivoEndpoints || [],
  });
  const fromDisplay = extensionDisplay?.from ?? (isDisplayInbound ? activeCustomerNumber : ourPhoneInfo.number);
  const activeCallerDisplay = extensionDisplay?.title || callerName;
  const fromFriendlyName = extensionDisplay ? "" : (isDisplayInbound ? "" : ourPhoneInfo.friendlyName);
  const toDisplay = extensionDisplay?.to ?? (isDisplayInbound ? ourPhoneInfo.number : activeCustomerNumber);
  const toFriendlyName = extensionDisplay ? "" : (isDisplayInbound ? ourPhoneInfo.friendlyName : "");

  const transferOptions = useMemo(() => {
    const options: Array<{ type: "extension" | "agent"; value: string; label: string; sublabel?: string }> = [];
    if (extensionsList) {
      for (const ext of extensionsList) {
        options.push({
          type: "extension",
          value: ext.extensionNumber,
          label: `Ext ${ext.extensionNumber}${ext.label ? ` - ${ext.label}` : ""}`,
          sublabel: ext.targetType === "agent" ? "Agent" : "External",
        });
      }
    }
    if (plivoEndpoints) {
      for (const ep of plivoEndpoints) {
        const alreadyHasExtension = extensionsList?.some((ext: any) => ext.targetEndpointId === ep.id);
        if (!alreadyHasExtension) {
          options.push({
            type: "agent",
            value: ep.id,
            label: ep.alias || ep.username,
            sublabel: "SIP Endpoint",
          });
        }
      }
    }
    return options;
  }, [extensionsList, plivoEndpoints]);

  const filteredTransferOptions = useMemo(() => {
    if (!transferSearch) return transferOptions;
    const search = transferSearch.toLowerCase();
    return transferOptions.filter(opt =>
      opt.label.toLowerCase().includes(search) ||
      opt.value.toLowerCase().includes(search) ||
      (opt.sublabel || "").toLowerCase().includes(search)
    );
  }, [transferOptions, transferSearch]);

  const cleanAndPrefixTransferNumber = useCallback((raw: string): string => {
    const cleaned = raw.replace(/[\s\-().]/g, "");
    if (!cleaned.startsWith("+") && tenantSettings?.defaultCountryCode) {
      return tenantSettings.defaultCountryCode + cleaned;
    }
    return cleaned;
  }, [tenantSettings?.defaultCountryCode]);

  const screenPopAutoOpen = tenantSettings?.tenantBusinessSettings?.screenPopAutoOpenOutbound ?? true;

  useEffect(() => {
    if (callState !== "idle" && activeContactId && !navigatedRef.current) {
      const isOutbound = callDirection === "outbound";
      const shouldNavigate = isOutbound ? screenPopAutoOpen : true;
      const onPreviewDialer = window.location.pathname.startsWith("/preview-dialer");
      if (shouldNavigate && !onPreviewDialer) {
        navigatedRef.current = true;
        navigate(`/contacts/${activeContactId}`);
      }
    }
    if (callState === "idle") {
      navigatedRef.current = false;
      setShowTransfer(false);
      setTransferTarget("");
      setShowVoicemailDrop(false);
      setSelectedVmDropRecording("");
      setVmDropSearch("");
      clearDncWarning();
    }
  }, [callState, activeContactId, navigate, clearDncWarning, callDirection, screenPopAutoOpen]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.number) {
        const num = detail.number;
        if (num.startsWith("+")) {
          const detected = detectCountryFromInput(num);
          if (detected) {
            setSelectedCountry(detected.country);
          }
        }
        setInputValue(num);
        open();
      }
    };
    window.addEventListener("dialpad:dial", handler);
    return () => window.removeEventListener("dialpad:dial", handler);
  }, [open]);

  const handleDigitPress = useCallback(
    (digit: string) => {
      if (callState === "in-progress") {
        sendDtmf(digit);
      } else {
        setInputValue((prev) => prev + digit);
      }
    },
    [callState, sendDtmf],
  );

  const handleBackspace = useCallback(() => {
    setInputValue((prev) => prev.slice(0, -1));
  }, []);

  const handleCall = useCallback(() => {
    if (inputValue.length > 0) {
      let fullNumber: string;
      const cleanInput = inputValue.replace(/[^0-9+*#]/g, "");
      const isExtension = /^\d{3,5}$/.test(cleanInput);
      if (inputValue.startsWith("+")) {
        fullNumber = cleanInput;
      } else if (isExtension) {
        fullNumber = cleanInput;
      } else {
        fullNumber = buildDialNumber(selectedCountry, inputValue);
      }
      const callerIdToUse =
        selectedCallerId !== "auto"
          ? selectedCallerId.startsWith("custom:")
            ? selectedCallerId.slice(7)
            : activePhoneNumbers.find((pn) => pn.id === selectedCallerId)?.number
          : undefined;
      makeCall(fullNumber, callerIdToUse);
    }
  }, [
    inputValue,
    makeCall,
    selectedCallerId,
    activePhoneNumbers,
    selectedCountry,
  ]);

  const digits = [
    { digit: "1", sub: "" },
    { digit: "2", sub: "ABC" },
    { digit: "3", sub: "DEF" },
    { digit: "4", sub: "GHI" },
    { digit: "5", sub: "JKL" },
    { digit: "6", sub: "MNO" },
    { digit: "7", sub: "PQRS" },
    { digit: "8", sub: "TUV" },
    { digit: "9", sub: "WXYZ" },
    { digit: "*", sub: "" },
    { digit: "0", sub: "+" },
    { digit: "#", sub: "" },
  ];

  const isInCall = callState === "in-progress" || callState === "ringing";
  const isIncoming = callState === "incoming";

  return (
    <>
      <div
        ref={dragConstraintsRef}
        className="fixed inset-0 pointer-events-none z-40"
      />
      <AnimatePresence>
        {isIncoming && !isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="pointer-events-auto">
            <Card className="p-6 w-[420px] border-2 border-green-500/30 bg-card shadow-[0_24px_56px_rgba(0,0,0,0.22),0_6px_16px_rgba(0,0,0,0.12)]">
              <div className="flex items-center gap-4 mb-5">
                <div className="relative shrink-0">
                  <Avatar className="w-14 h-14">
                    <AvatarFallback className="bg-green-500/10 text-green-600 dark:text-green-400 w-14 h-14">
                      <PhoneIncoming className="w-6 h-6" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xl font-semibold truncate"
                    data-testid="text-incoming-caller"
                  >
                    {callerName || "Unknown Caller"}
                  </p>
                  <p className="text-base text-muted-foreground">Incoming call</p>
                  {(fromDisplay || toDisplay || incomingCampaignName || incomingQueueName || incomingIvrMenuName) && (
                    <div className="text-base text-muted-foreground space-y-0.5 mt-1 font-sans" data-testid="div-from-to-incoming">
                      {incomingCampaignName && <div className="flex gap-1"><span className="font-semibold shrink-0">PD:</span><span className="break-words min-w-0">{incomingCampaignName}</span></div>}
                      {incomingQueueName && <div className="flex gap-1"><span className="font-semibold shrink-0">Queue:</span><span className="break-words min-w-0">{incomingQueueName}</span></div>}
                      {incomingIvrMenuName && <div className="flex gap-1"><span className="font-semibold shrink-0">IVR:</span><span className="break-words min-w-0">{incomingIvrMenuName}</span></div>}
                      {fromDisplay && <div className="flex gap-1"><span className="font-semibold shrink-0">FR:</span><span className="break-words min-w-0">{fromDisplay}</span></div>}
                      {toDisplay && <div className="flex gap-1"><span className="font-semibold shrink-0">TO:</span><span className="break-words min-w-0">{toDisplay}</span></div>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  size="lg"
                  className="flex-1 text-base"
                  onClick={rejectCall}
                  data-testid="button-reject-incoming"
                >
                  <PhoneOff className="w-5 h-5 mr-2" />
                  Decline
                </Button>
                <Button
                  size="lg"
                  className="flex-1 bg-green-600 text-white hover-elevate text-base"
                  onClick={() => {
                    answerCall();
                    open();
                  }}
                  data-testid="button-answer-incoming"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Answer
                </Button>
              </div>
            </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            drag
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed top-16 right-6 z-[9999]"
            style={{ touchAction: "none", filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.2)) drop-shadow(0 4px 12px rgba(0,0,0,0.14))" }}
          >
            {isMinimized ? (
              <div
                className="w-72 rounded-md shadow-lg cursor-grab active:cursor-grabbing overflow-visible"
                style={{
                  background:
                    "linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)",
                }}
                data-testid="card-minimized-dialpad"
              >
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer"
                  onClick={() => {
                    if (!isDragging) restore();
                  }}
                >
                  <div className="text-white/60">
                    <GripHorizontal className="w-4 h-4" />
                  </div>
                  {isInCall || isIncoming ? (
                    <>
                      <div className="relative">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-white/20 text-white text-xs">
                            <User className="w-3 h-3" />
                          </AvatarFallback>
                        </Avatar>
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse border border-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {activeCallerDisplay || "Unknown"}
                        </p>
                        <p className="text-xs text-white/70 font-mono">
                          {callState === "ringing"
                            ? "Ringing..."
                            : callState === "incoming"
                              ? "Incoming..."
                              : formatDuration(callDuration)}
                        </p>
                        {(fromDisplay || toDisplay || incomingCampaignName || incomingQueueName || incomingIvrMenuName) && (
                          <div className="text-[10px] text-white/60 space-y-0 mt-0.5 font-sans" data-testid="div-from-to-mini">
                            {incomingCampaignName && <div className="flex gap-1"><span className="font-semibold shrink-0">PD:</span><span className="break-words min-w-0">{incomingCampaignName}</span></div>}
                            {incomingQueueName && <div className="flex gap-1"><span className="font-semibold shrink-0">Queue:</span><span className="break-words min-w-0">{incomingQueueName}</span></div>}
                            {incomingIvrMenuName && <div className="flex gap-1"><span className="font-semibold shrink-0">IVR:</span><span className="break-words min-w-0">{incomingIvrMenuName}</span></div>}
                            {fromDisplay && <div className="flex gap-1"><span className="font-semibold shrink-0">FR:</span><span className="break-words min-w-0">{fromDisplay}</span></div>}
                            {toDisplay && <div className="flex gap-1"><span className="font-semibold shrink-0">TO:</span><span className="break-words min-w-0">{toDisplay}</span></div>}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <Phone className="w-4 h-4 text-white/80" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">
                          Dialpad
                        </p>
                        {callEndNotification ? (
                          <p className={`text-xs font-medium flex items-center gap-1 flex-wrap leading-tight ${
                            callEndNotification.type === "red" ? "text-red-300" : "text-yellow-300"
                          }`}>
                            <AlertTriangle className="w-3 h-3 shrink-0" />
                            <span>{callEndNotification.message}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-white/70">
                            {isRegistered ? "Connected" : "Offline"}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white/80 hover:text-white hover:bg-white/15"
                    onClick={(e) => {
                      e.stopPropagation();
                      restore();
                    }}
                    data-testid="button-expand-dialpad"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white/80 hover:text-white hover:bg-white/15"
                    onClick={(e) => {
                      e.stopPropagation();
                      close();
                    }}
                    data-testid="button-close-minimized"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Card className="w-72 overflow-visible shadow-2xl border border-border/60 bg-background">
                <div className="flex items-center justify-between p-3 gap-2 border-b cursor-grab active:cursor-grabbing">
                  <div className="flex items-center gap-2">
                    <GripHorizontal className="w-4 h-4 text-muted-foreground" />
                    <Badge
                      variant={isRegistered ? "default" : "destructive"}
                      className="text-xs"
                      data-testid="badge-registration-status"
                    >
                      {isRegistered ? "Connected" : "Offline"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className={`toggle-elevate ${showAudioSettings ? "toggle-elevated" : ""}`}
                      onClick={() => setShowAudioSettings(!showAudioSettings)}
                      data-testid="button-audio-settings"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => minimize()}
                      data-testid="button-minimize-dialpad"
                    >
                      <Minimize2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => close()}
                      data-testid="button-close-dialpad"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>


                <Dialog
                  open={showAudioSettings}
                  onOpenChange={(open) => {
                    if (!open && testingMic) {
                      stopMicTest();
                    }
                    setShowAudioSettings(open);
                  }}
                >
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-base">
                        <Settings className="w-4 h-4" />
                        Dial Pad Settings
                      </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-sm font-medium">
                          <Mic className="w-3.5 h-3.5 text-muted-foreground" />
                          Microphone
                        </label>
                        <Select
                          value={selectedMic}
                          onValueChange={applyMicDevice}
                        >
                          <SelectTrigger
                            className="text-xs"
                            data-testid="select-microphone"
                          >
                            <SelectValue placeholder="Select microphone" />
                          </SelectTrigger>
                          <SelectContent>
                            {audioDevices.microphones.map((d) => (
                              <SelectItem
                                key={d.deviceId}
                                value={d.deviceId || "default"}
                              >
                                {d.label || "Default Microphone"}
                              </SelectItem>
                            ))}
                            {audioDevices.microphones.length === 0 && (
                              <SelectItem value="default">
                                Default Microphone
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={toggleMicTest}
                            data-testid="button-test-mic"
                          >
                            <Mic className="w-3 h-3 mr-1.5" />
                            {testingMic ? "Stop Test" : "Test"}
                          </Button>
                          {testingMic && (
                            <div className="flex-1 flex items-center gap-1.5">
                              <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-75"
                                  style={{
                                    width: `${micLevel}%`,
                                    backgroundColor:
                                      micLevel > 70
                                        ? "hsl(var(--destructive))"
                                        : micLevel > 40
                                          ? "hsl(var(--chart-4))"
                                          : "hsl(var(--primary))",
                                  }}
                                />
                              </div>
                              <span className="text-[10px] text-muted-foreground font-mono w-7 text-right">
                                {micLevel}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-sm font-medium">
                          <Bell className="w-3.5 h-3.5 text-muted-foreground" />
                          Ringtone Speaker
                        </label>
                        <Select
                          value={selectedRingtone}
                          onValueChange={applyRingtoneDevice}
                        >
                          <SelectTrigger
                            className="text-xs"
                            data-testid="select-ringtone-speaker"
                          >
                            <SelectValue placeholder="Select ringtone speaker" />
                          </SelectTrigger>
                          <SelectContent>
                            {audioDevices.speakers.map((d) => (
                              <SelectItem
                                key={d.deviceId}
                                value={d.deviceId || "default"}
                              >
                                {d.label || "Default Speaker"}
                              </SelectItem>
                            ))}
                            {audioDevices.speakers.length === 0 && (
                              <SelectItem value="default">
                                Default Speaker
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => playTestRingtone("ringtone")}
                          disabled={testingRingtone}
                          data-testid="button-test-ringtone"
                        >
                          <Bell className="w-3 h-3 mr-1.5" />
                          {testingRingtone ? "Ringing..." : "Test Ringtone"}
                        </Button>
                      </div>

                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-sm font-medium">
                          <Volume2 className="w-3.5 h-3.5 text-muted-foreground" />
                          Output Speaker
                        </label>
                        <Select
                          value={selectedSpeaker}
                          onValueChange={applySpeakerDevice}
                        >
                          <SelectTrigger
                            className="text-xs"
                            data-testid="select-output-speaker"
                          >
                            <SelectValue placeholder="Select output speaker" />
                          </SelectTrigger>
                          <SelectContent>
                            {audioDevices.speakers.map((d) => (
                              <SelectItem
                                key={d.deviceId}
                                value={d.deviceId || "default"}
                              >
                                {d.label || "Default Speaker"}
                              </SelectItem>
                            ))}
                            {audioDevices.speakers.length === 0 && (
                              <SelectItem value="default">
                                Default Speaker
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => playTestRingtone("speaker")}
                          disabled={testingSpeaker}
                          data-testid="button-test-speaker"
                        >
                          <Volume2 className="w-3 h-3 mr-1.5" />
                          {testingSpeaker ? "Playing..." : "Test Speaker"}
                        </Button>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => loadAudioDevices(true)}
                          disabled={refreshingDevices}
                          data-testid="button-refresh-devices"
                        >
                          <RefreshCw
                            className={`w-3 h-3 mr-1.5 ${refreshingDevices ? "animate-spin" : ""}`}
                          />
                          {refreshingDevices
                            ? "Detecting..."
                            : "Detect & Setup"}
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={saveAllSettings}
                          data-testid="button-save-audio-settings"
                        >
                          {settingsSaved ? (
                            <>
                              <Check className="w-3 h-3 mr-1.5" />
                              Settings Saved
                            </>
                          ) : (
                            <>
                              <Save className="w-3 h-3 mr-1.5" />
                              Save Settings & Close
                            </>
                          )}
                        </Button>
                      </div>

                      {(localStorage.getItem("dialpad_mic") ||
                        localStorage.getItem("dialpad_speaker")) && (
                        <div className="rounded-md bg-muted/50 p-2.5 space-y-1">
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            Saved Device Configuration
                          </p>
                          <div className="space-y-0.5">
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                              <Mic className="w-3 h-3 shrink-0" />
                              <span className="truncate">
                                {audioDevices.microphones.find(
                                  (d) => d.deviceId === selectedMic,
                                )?.label || "Default Microphone"}
                              </span>
                            </p>
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                              <Bell className="w-3 h-3 shrink-0" />
                              <span className="truncate">
                                {audioDevices.speakers.find(
                                  (d) => d.deviceId === selectedRingtone,
                                )?.label || "Default Speaker"}
                              </span>
                            </p>
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                              <Volume2 className="w-3 h-3 shrink-0" />
                              <span className="truncate">
                                {audioDevices.speakers.find(
                                  (d) => d.deviceId === selectedSpeaker,
                                )?.label || "Default Speaker"}
                              </span>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                {isInCall || isIncoming ? (
                  <div className="p-4 cursor-default">
                    <div className="text-center mb-4">
                      <p
                        className="text-lg font-semibold"
                        data-testid="text-active-caller"
                      >
                        {activeCallerDisplay || "Unknown"}
                      </p>
                      <p
                        className="text-sm text-muted-foreground font-mono"
                        data-testid="text-call-status"
                      >
                        {callState === "ringing" && "Ringing..."}
                        {callState === "incoming" && "Incoming call..."}
                        {callState === "in-progress" &&
                          formatDuration(callDuration)}
                      </p>
                      {(fromDisplay || toDisplay || incomingCampaignName || incomingQueueName || incomingIvrMenuName) && (
                        <div className="mt-2 text-left text-xs text-muted-foreground space-y-0.5 mx-auto w-fit" data-testid="div-from-to">
                          {incomingCampaignName && (
                            <div className="flex gap-2 font-sans" data-testid="div-pd-campaign">
                              <span className="font-medium shrink-0">PD:</span>
                              <span className="break-words min-w-0" data-testid="text-pd-campaign-name">{incomingCampaignName}</span>
                            </div>
                          )}
                          {incomingQueueName && (
                            <div className="flex gap-2 font-sans">
                              <span className="font-medium shrink-0">Queue:</span>
                              <span className="break-words min-w-0" data-testid="text-queue-name">{incomingQueueName}</span>
                            </div>
                          )}
                          {incomingIvrMenuName && (
                            <div className="flex gap-2 font-sans" data-testid="div-routing">
                              <span className="font-medium shrink-0">IVR:</span>
                              <span className="break-words min-w-0" data-testid="text-ivr-name">{incomingIvrMenuName}</span>
                            </div>
                          )}
                          {fromDisplay && (
                            <div className="flex gap-2">
                              <span className="w-6 font-medium shrink-0">FR:</span>
                              <div className="flex flex-col min-w-0">
                                <span className="font-sans break-words" data-testid="text-from-number">{fromDisplay}</span>
                                {fromFriendlyName && <span className="text-xs text-muted-foreground">{fromFriendlyName}</span>}
                              </div>
                            </div>
                          )}
                          {toDisplay && (
                            <div className="flex gap-2">
                              <span className="w-6 font-medium shrink-0">TO:</span>
                              <div className="flex flex-col min-w-0">
                                <span className="font-sans break-words" data-testid="text-to-number">{toDisplay}</span>
                                {toFriendlyName && <span className="text-xs text-muted-foreground">{toFriendlyName}</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {callState === "in-progress" &&
                      !showTransfer &&
                      !showVoicemailDrop &&
                      warmTransferPhase === "idle" && (
                        <>
                        <div className="grid grid-cols-3 gap-1.5 mb-3">
                          {digits.map(({ digit, sub }) => (
                            <Button
                              key={digit}
                              variant="ghost"
                              className="h-11 flex flex-col gap-0 rounded-md"
                              onClick={() => handleDigitPress(digit)}
                              data-testid={`button-dtmf-${digit}`}
                            >
                              <span className="text-base font-medium leading-none">
                                {digit}
                              </span>
                              {sub && (
                                <span className="text-[9px] text-muted-foreground tracking-widest leading-none mt-0.5">
                                  {sub}
                                </span>
                              )}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          className="w-full mb-3"
                          size="sm"
                          onClick={() => {
                            const defaultRecId = userSettings?.voicemailDropEnabled && userSettings?.voicemailDropRecordingId;
                            const defaultExistsInTenant = defaultRecId && (vmDropRecordings || []).some(r => r.id === userSettings.voicemailDropRecordingId);
                            if (defaultExistsInTenant && currentCallUuid) {
                              voicemailDropMutation.mutate({ callUuid: currentCallUuid, recordingId: userSettings.voicemailDropRecordingId! });
                            } else {
                              setShowVoicemailDrop(true);
                              setShowTransfer(false);
                              setVmDropSearch("");
                              setSelectedVmDropRecording("");
                            }
                          }}
                          disabled={voicemailDropMutation.isPending}
                          data-testid="button-vmdrop-toggle"
                        >
                          {voicemailDropMutation.isPending ? (
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          ) : (
                            <Voicemail className="w-4 h-4 mr-1.5" />
                          )}
                          {voicemailDropMutation.isPending ? "Dropping..." : "VM Drop"}
                        </Button>
                        </>
                      )}

                    {callState === "in-progress" &&
                      showTransfer &&
                      warmTransferPhase === "idle" && (
                        <div className="mb-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setShowTransfer(false)}
                              data-testid="button-transfer-back"
                            >
                              <ArrowLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-sm font-medium">
                              Transfer Call
                            </span>
                          </div>

                          <div className="flex gap-1 rounded-md bg-muted/50 p-1">
                            <Button
                              variant={
                                transferType === "cold" ? "default" : "ghost"
                              }
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={() => setTransferType("cold")}
                              data-testid="button-transfer-cold"
                            >
                              Cold
                            </Button>
                            <Button
                              variant={
                                transferType === "warm" ? "default" : "ghost"
                              }
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={() => setTransferType("warm")}
                              data-testid="button-transfer-warm"
                            >
                              Warm
                            </Button>
                          </div>

                          <p className="text-xs text-muted-foreground">
                            {transferType === "cold"
                              ? "Caller is transferred immediately. Your call ends."
                              : "Customer is placed on hold while you consult with the target. Merge when ready."}
                          </p>

                          <Popover open={transferDropdownOpen} onOpenChange={setTransferDropdownOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-between text-xs h-9"
                                data-testid="button-transfer-target-select"
                              >
                                <span className="truncate">
                                  {transferTarget
                                    ? (() => {
                                        const opt = transferOptions.find(o => o.value === transferTarget);
                                        return opt ? opt.label : transferTarget;
                                      })()
                                    : "Select agent or enter phone number"}
                                </span>
                                <ChevronDown className="w-3 h-3 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start" onCloseAutoFocus={(e) => e.preventDefault()} style={{ zIndex: 10000 }}>
                              <div className="flex items-center gap-2 px-2 py-1.5 border-b">
                                <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                <input
                                  className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60"
                                  placeholder="Search extensions, agents, or enter number..."
                                  value={transferSearch}
                                  onChange={(e) => setTransferSearch(e.target.value)}
                                  data-testid="input-transfer-search"
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                <div className="p-1">
                                  {transferSearch && /^\+?[\d\s\-().]{1,}$/.test(transferSearch.trim()) && (
                                    <button
                                      className="flex items-center gap-2 w-full px-2.5 py-3 text-xs rounded-md cursor-pointer text-left bg-primary/10 hover:bg-primary/20 font-medium mb-1 border border-primary/20"
                                      onClick={() => {
                                        setTransferTarget(cleanAndPrefixTransferNumber(transferSearch));
                                        setTransferDropdownOpen(false);
                                        setTransferSearch("");
                                      }}
                                      data-testid="transfer-option-manual-number"
                                    >
                                      <Phone className="w-4 h-4 shrink-0 text-primary" />
                                      <div className="flex flex-col">
                                        <span>Dial external number</span>
                                        <span className="text-primary font-bold text-sm">{cleanAndPrefixTransferNumber(transferSearch)}</span>
                                      </div>
                                    </button>
                                  )}
                                  {filteredTransferOptions.map((opt) => (
                                    <button
                                      key={`${opt.type}-${opt.value}`}
                                      className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-sm hover-elevate cursor-pointer text-left"
                                      onClick={() => {
                                        setTransferTarget(opt.value);
                                        setTransferDropdownOpen(false);
                                        setTransferSearch("");
                                      }}
                                      data-testid={`transfer-option-${opt.value}`}
                                    >
                                      <Check className={`w-3 h-3 shrink-0 ${transferTarget === opt.value ? "opacity-100" : "opacity-0"}`} />
                                      <div className="flex flex-col min-w-0">
                                        <span className="truncate">{opt.label}</span>
                                        {opt.sublabel && <span className="text-[10px] text-muted-foreground">{opt.sublabel}</span>}
                                      </div>
                                    </button>
                                  ))}
                                  {filteredTransferOptions.length === 0 && transferSearch && !/^\+?\d{1,}$/.test(transferSearch) && (
                                    <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                                      No matches for "{transferSearch}"
                                    </div>
                                  )}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>

                          {transferError && (
                            <p className="text-xs text-destructive">
                              {transferError}
                            </p>
                          )}

                          <Button
                            className="w-full"
                            size="sm"
                            disabled={!transferTarget || isTransferring}
                            onClick={async () => {
                              await transferCall(
                                transferTarget,
                                transferType,
                                transferOptions.find(o => o.value === transferTarget)?.label,
                              );
                              if (transferType === "cold") {
                                setShowTransfer(false);
                                setTransferTarget("");
                              }
                            }}
                            data-testid="button-transfer-confirm"
                          >
                            {isTransferring ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <PhoneForwarded className="w-4 h-4 mr-1" />
                            )}
                            {isTransferring
                              ? "Transferring..."
                              : `${transferType === "cold" ? "Cold" : "Warm"} Transfer`}
                          </Button>
                        </div>
                      )}

                    {callState === "in-progress" &&
                      warmTransferPhase === "consultation" && (
                        <div className="mb-4 space-y-3">
                          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3">
                            <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                              Consulting with {warmTransferTarget}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Customer is on hold. Speak privately with the
                              target, then connect the contact or cancel.
                            </p>
                          </div>

                          <div className="grid grid-cols-3 gap-1 mb-1">
                            {digits.map(({ digit, sub }) => (
                              <Button
                                key={digit}
                                variant="ghost"
                                className="h-9 flex flex-col gap-0 rounded-md"
                                onClick={() => handleDigitPress(digit)}
                                data-testid={`button-transfer-dtmf-${digit}`}
                              >
                                <span className="text-sm font-medium leading-none">
                                  {digit}
                                </span>
                                {sub && (
                                  <span className="text-[8px] text-muted-foreground tracking-widest leading-none mt-0.5">
                                    {sub}
                                  </span>
                                )}
                              </Button>
                            ))}
                          </div>

                          {transferError && (
                            <p className="text-xs text-destructive">
                              {transferError}
                            </p>
                          )}

                          <div className="flex gap-2">
                            <Button
                              className="flex-1"
                              size="sm"
                              variant="default"
                              disabled={isTransferring}
                              onClick={mergeTransfer}
                              data-testid="button-transfer-connect-contact"
                            >
                              {isTransferring ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Users className="w-4 h-4 mr-1" />
                              )}
                              Connect Contact
                            </Button>
                            <Button
                              className="flex-1"
                              size="sm"
                              variant="outline"
                              disabled={isTransferring}
                              onClick={cancelTransfer}
                              data-testid="button-transfer-cancel"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                    {/* Req 9: "cancelling" phase shows normal call UI immediately (no amber panel).
                        Server reconciliation and polling continue silently in the background. */}

                    {callState === "in-progress" &&
                      warmTransferPhase === "connected" && (
                        <div className="mb-4 space-y-3">
                          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3">
                            <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                              All parties connected
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Customer, you, and {warmTransferTarget} are in a
                              3-way conference. Click Complete to leave the call.
                            </p>
                          </div>

                          {transferError && (
                            <p className="text-xs text-destructive">
                              {transferError}
                            </p>
                          )}

                          <Button
                            className="w-full"
                            size="sm"
                            variant="default"
                            disabled={isTransferring}
                            onClick={completeTransfer}
                            data-testid="button-transfer-complete"
                          >
                            {isTransferring ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            )}
                            Complete Transfer
                          </Button>
                        </div>
                      )}

                    {callState === "in-progress" &&
                      showVoicemailDrop &&
                      warmTransferPhase === "idle" && (
                        <div className="mb-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => { setShowVoicemailDrop(false); setVmDropSearch(""); }}
                              data-testid="button-vmdrop-back"
                            >
                              <ArrowLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-sm font-medium">
                              Voicemail Drop
                            </span>
                          </div>

                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <input
                              type="text"
                              placeholder="Search recordings..."
                              value={vmDropSearch}
                              onChange={(e) => setVmDropSearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                              data-testid="input-vmdrop-search"
                            />
                          </div>

                          {(() => {
                            const defaultRecId = userSettings?.voicemailDropRecordingId;
                            const filtered = (vmDropRecordings || []).filter(r =>
                              r.name.toLowerCase().includes(vmDropSearch.toLowerCase())
                            ).sort((a, b) => {
                              if (a.id === defaultRecId) return -1;
                              if (b.id === defaultRecId) return 1;
                              return 0;
                            });
                            return filtered.length > 0 ? (
                              <ScrollArea className="max-h-36">
                                <div className="space-y-1">
                                  {filtered.map((rec) => (
                                    <button
                                      key={rec.id}
                                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs hover-elevate cursor-pointer text-left ${
                                        selectedVmDropRecording === rec.id ? "bg-accent" : ""
                                      }`}
                                      onClick={() => setSelectedVmDropRecording(rec.id)}
                                      data-testid={`vmdrop-recording-${rec.id}`}
                                    >
                                      <Check className={`w-3 h-3 shrink-0 ${selectedVmDropRecording === rec.id ? "opacity-100" : "opacity-0"}`} />
                                      <span className="truncate">{rec.name}</span>
                                      {rec.id === defaultRecId && (
                                        <span className="ml-auto text-[10px] text-muted-foreground shrink-0">Default</span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </ScrollArea>
                            ) : (
                              <p className="text-xs text-muted-foreground py-2 text-center">
                                {vmDropSearch ? "No matching recordings" : "No voicemail drop recordings available"}
                              </p>
                            );
                          })()}

                          <Button
                            className="w-full"
                            size="sm"
                            disabled={!selectedVmDropRecording || !currentCallUuid || voicemailDropMutation.isPending}
                            onClick={() => {
                              if (currentCallUuid && selectedVmDropRecording) {
                                voicemailDropMutation.mutate({ callUuid: currentCallUuid, recordingId: selectedVmDropRecording });
                              }
                            }}
                            data-testid="button-vmdrop-confirm"
                          >
                            {voicemailDropMutation.isPending ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Volume2 className="w-4 h-4 mr-1" />
                            )}
                            {voicemailDropMutation.isPending ? "Dropping..." : "Drop Voicemail"}
                          </Button>
                        </div>
                      )}

                    <div className="flex items-center justify-center gap-3">
                      {callState === "incoming" ? (
                        <>
                          <Button
                            variant="destructive"
                            size="icon"
                            className="w-14 h-14 rounded-full"
                            onClick={rejectCall}
                            data-testid="button-reject-call"
                          >
                            <PhoneOff className="w-6 h-6" />
                          </Button>
                          <Button
                            size="icon"
                            className="w-14 h-14 rounded-full bg-green-600 text-white hover-elevate"
                            onClick={() => answerCall()}
                            data-testid="button-answer-call"
                          >
                            <Phone className="w-6 h-6" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant={isMuted ? "default" : "ghost"}
                            size="icon"
                            className={`w-14 h-14 rounded-full ${isOnHold ? "opacity-40 cursor-not-allowed" : ""}`}
                            onClick={toggleMute}
                            title={isOnHold ? "Cannot unmute while customer is on hold" : isMuted ? "Unmute" : "Mute"}
                            data-testid="button-toggle-mute"
                            disabled={isOnHold}
                          >
                            {isMuted ? (
                              <MicOff className="w-6 h-6" />
                            ) : (
                              <Mic className="w-6 h-6" />
                            )}
                          </Button>
                          <Button
                            variant={isOnHold ? "default" : "ghost"}
                            size="icon"
                            className={`w-14 h-14 rounded-full ${isOnHold ? "bg-amber-500 hover:bg-amber-600 text-white" : "text-muted-foreground"}`}
                            onClick={toggleHold}
                            title={isOnHold ? "Resume Call" : "Hold"}
                            data-testid="button-toggle-hold"
                          >
                            <Pause className="w-6 h-6" />
                          </Button>
                          {stopRecordingButtonEnabled && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`w-14 h-14 rounded-full text-muted-foreground`}
                              onClick={() => isRecordingPaused ? resumeRecording() : pauseRecording()}
                              title={isRecordingPaused ? "Resume Recording" : "Pause Recording"}
                              data-testid="button-toggle-recording"
                            >
                              {isRecordingPaused ? (
                                <CirclePlay className="w-6 h-6" />
                              ) : (
                                <CirclePause className="w-6 h-6" />
                              )}
                            </Button>
                          )}
                          {warmTransferPhase === "idle" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-14 h-14 rounded-full"
                              onClick={() => { setShowTransfer(!showTransfer); setShowVoicemailDrop(false); }}
                              data-testid="button-transfer-toggle"
                            >
                              <PhoneForwarded className="w-6 h-6" />
                            </Button>
                          )}
                          {tenantSettings?.tenantBusinessSettings?.csatEnabled &&
                            tenantSettings?.tenantBusinessSettings?.csatSameCallEnabled &&
                            csatCallDirectionEligible &&
                            callState === "in-progress" &&
                            warmTransferPhase === "idle" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-14 h-14 rounded-full text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                              onClick={() => {
                                setCsatPickerTemplateId("__auto__");
                                setShowCsatPickerDialog(true);
                              }}
                              data-testid="button-csat-survey-transfer"
                            >
                              <SmilePlus className="w-6 h-6" />
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="icon"
                            className="w-14 h-14 rounded-full"
                            onClick={hangupCall}
                            data-testid="button-hangup-call"
                          >
                            <PhoneOff className="w-6 h-6" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 cursor-default">
                    {isDispositionBlocked && (
                      <div
                        className="flex items-center gap-2 mb-3 rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-amber-700 dark:text-amber-400"
                        data-testid="text-disposition-blocked-warning"
                      >
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span className="text-xs">
                          Complete disposition before making calls
                        </span>
                      </div>
                    )}
                    {callEndNotification && callState === "idle" && (
                      <div
                        className={`flex items-center gap-1.5 px-3 py-2 mb-3 rounded-md text-xs flex-wrap ${
                          callEndNotification.type === "red"
                            ? "bg-destructive/10 text-destructive"
                            : "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400"
                        }`}
                        data-testid="div-call-end-notification"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span>
                          {callEndNotification.message}
                          {(user?.role === "super_admin" || user?.role === "tenant_admin" || ((user?.role as string) === "admin")) && lastCallEndReason && !CALL_END_NOTIFICATIONS[lastCallEndReason] && (
                            <span className="font-mono opacity-70 ml-1">({lastCallEndReason})</span>
                          )}
                        </span>
                      </div>
                    )}

                    <div className="flex gap-1 mb-3 bg-muted/50 rounded-md p-0.5">
                      <button
                        className={`flex-1 text-xs py-1 rounded-sm font-medium transition-colors ${dialTab === "phone" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        onClick={() => setDialTab("phone")}
                        data-testid="tab-phone-number"
                      >
                        Phone Number
                      </button>
                      <button
                        className={`flex-1 text-xs py-1 rounded-sm font-medium transition-colors ${dialTab === "extension" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        onClick={() => setDialTab("extension")}
                        data-testid="tab-extensions"
                      >
                        Extensions
                      </button>
                    </div>

                    {dialTab === "extension" && (
                      <div className="mb-3">
                        <Popover open={extensionDropdownOpen} onOpenChange={(open) => { setExtensionDropdownOpen(open); if (!open) setExtensionSearch(""); }}>
                          <PopoverTrigger asChild>
                            <button
                              className="flex items-center justify-between w-full min-h-[2.5rem] rounded-md bg-muted/50 px-3 text-sm hover:bg-muted/80 transition-colors"
                              data-testid="button-extension-selector"
                            >
                              {selectedExtension ? (() => {
                                const ext = extensionsList?.find((e: any) => e.extensionNumber === selectedExtension);
                                return (
                                  <div className="flex flex-col items-start min-w-0 text-left py-0.5">
                                    <span className="font-medium truncate">Ext {ext?.extensionNumber ?? selectedExtension}</span>
                                    {ext?.label && <span className="text-xs text-muted-foreground truncate">{ext.label}</span>}
                                  </div>
                                );
                              })() : (
                                <span className="text-muted-foreground/60">Select extension to call...</span>
                              )}
                              <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-1" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start" onCloseAutoFocus={(e) => e.preventDefault()} style={{ zIndex: 10000 }}>
                            <div className="flex items-center gap-2 px-2 py-1.5 border-b">
                              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <input
                                className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60"
                                placeholder="Search extensions..."
                                value={extensionSearch}
                                onChange={(e) => setExtensionSearch(e.target.value)}
                                data-testid="input-extension-search"
                                autoFocus
                              />
                            </div>
                            <ScrollArea className="h-52">
                              <div className="p-1">
                                {(() => {
                                  if (!extensionsList || extensionsList.length === 0) {
                                    return <p className="py-6 text-center text-sm text-muted-foreground">No extensions configured</p>;
                                  }
                                  const filtered = extensionsList.filter((ext: any) => {
                                    if (!extensionSearch) return true;
                                    const q = extensionSearch.toLowerCase();
                                    return String(ext.extensionNumber).includes(q) || (ext.label || "").toLowerCase().includes(q);
                                  });
                                  if (filtered.length === 0) {
                                    return <p className="py-6 text-center text-sm text-muted-foreground">No results</p>;
                                  }
                                  return filtered.map((ext: any) => (
                                    <button
                                      key={ext.extensionNumber}
                                      className="flex items-center gap-2 w-full px-2 py-2 text-sm rounded-sm hover-elevate cursor-pointer text-left"
                                      onClick={() => { setSelectedExtension(ext.extensionNumber); setExtensionDropdownOpen(false); setExtensionSearch(""); }}
                                      data-testid={`option-extension-${ext.extensionNumber}`}
                                    >
                                      <Check className={`w-3 h-3 shrink-0 ${selectedExtension === ext.extensionNumber ? "opacity-100" : "opacity-0"}`} />
                                      <div className="flex flex-col min-w-0">
                                        <span className="font-medium truncate">Ext {ext.extensionNumber}</span>
                                        {ext.label && <span className="text-xs text-muted-foreground truncate">{ext.label}</span>}
                                      </div>
                                    </button>
                                  ));
                                })()}
                              </div>
                            </ScrollArea>
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    {dialTab === "phone" && <div className="flex items-center gap-1 mb-3 min-h-[2.5rem] rounded-md bg-muted/50 px-2">
                      <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                        <PopoverTrigger asChild>
                          <button
                            className="flex items-center gap-1 shrink-0 px-1 py-1 rounded hover-elevate cursor-pointer"
                            data-testid="button-country-selector"
                          >
                            <img
                              src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
                              alt={selectedCountry.name}
                              className="w-5 h-3.5 object-cover rounded-[2px]"
                            />
                            <span className="text-sm font-mono text-muted-foreground">
                              {selectedCountry.dialCode}
                            </span>
                            <ChevronDown className="w-3 h-3 opacity-50" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-64 p-0"
                          align="start"
                          side="bottom"
                          style={{ zIndex: 10000 }}
                        >
                          <div className="flex items-center gap-2 px-3 py-2 border-b">
                            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <input
                              type="text"
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              placeholder="Search country..."
                              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
                              data-testid="input-country-search"
                            />
                          </div>
                          <ScrollArea className="h-56">
                            <div className="p-1">
                              {filteredCountries.map((c) => (
                                <button
                                  key={c.code}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm hover-elevate cursor-pointer ${
                                    selectedCountry.code === c.code
                                      ? "bg-accent"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    setSelectedCountry(c);
                                    setCountryOpen(false);
                                    setCountrySearch("");
                                  }}
                                  data-testid={`option-country-${c.code}`}
                                >
                                  <img
                                    src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                                    alt={c.name}
                                    title={c.name}
                                    className="w-5 h-3.5 object-cover rounded-[2px] shrink-0"
                                  />
                                  <span
                                    className="flex-1 min-w-0 text-left truncate"
                                    title={c.name}
                                  >
                                    {c.code}
                                  </span>
                                  <span className="text-muted-foreground font-mono text-xs shrink-0 whitespace-nowrap">
                                    {c.dialCode}
                                  </span>
                                  {selectedCountry.code === c.code && (
                                    <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                                  )}
                                </button>
                              ))}
                              {filteredCountries.length === 0 && (
                                <p className="py-4 text-center text-sm text-muted-foreground">
                                  No country found
                                </p>
                              )}
                            </div>
                          </ScrollArea>
                        </PopoverContent>
                      </Popover>
                      <input
                        type="text"
                        inputMode="tel"
                        value={inputValue}
                        onChange={(e) =>
                          setInputValue(e.target.value.replace(/[^0-9+*#\s().-]/g, ""))
                        }
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData("text");
                          const sanitized = pasted.replace(/[^0-9+*#\s().-]/g, "");
                          if (sanitized !== pasted) {
                            e.preventDefault();
                            setInputValue((prev) => prev + sanitized);
                          }
                        }}
                        placeholder="Enter phone number"
                        className="flex-1 bg-transparent text-sm font-mono text-left outline-none placeholder:text-muted-foreground/50 min-w-0"
                        data-testid="input-phone-number"
                      />
                      {inputValue && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="shrink-0"
                          onClick={handleBackspace}
                          data-testid="button-backspace"
                        >
                          <Delete className="w-4 h-4" />
                        </Button>
                      )}
                    </div>}

                    {(activePhoneNumbers.length > 0 || (customCallerIdsFeatureEnabled && (customCallerIdsList || []).length > 0)) && (
                      <div className="mb-3">
                        <Popover open={callerIdOpen} onOpenChange={(open) => { setCallerIdOpen(open); if (!open) setCallerIdSearch(""); }}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between text-xs font-normal h-auto py-1.5 min-h-[2rem]"
                              data-testid="select-caller-id"
                            >
                              <span className="truncate" title={callerIdLabel}>{callerIdLabel}</span>
                              <ChevronDown className="w-3 h-3 shrink-0 opacity-50 ml-1" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start" onCloseAutoFocus={(e) => e.preventDefault()} style={{ zIndex: 10000 }}>
                            <div className="flex items-center gap-2 px-2 py-1.5 border-b">
                              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <input
                                className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/60"
                                placeholder="Search by number or name..."
                                value={callerIdSearch}
                                onChange={(e) => setCallerIdSearch(e.target.value)}
                                data-testid="input-caller-id-search"
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              <div className="p-1">
                                {(!callerIdSearch || "default".includes(callerIdSearch.toLowerCase()) || "auto".includes(callerIdSearch.toLowerCase())) && (
                                  <button
                                    className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-sm hover-elevate cursor-pointer text-left"
                                    onClick={() => { setSelectedCallerId("auto"); setCallerIdOpen(false); setCallerIdSearch(""); }}
                                    data-testid="caller-id-option-auto"
                                  >
                                    <Check className={`w-3 h-3 shrink-0 ${selectedCallerId === "auto" ? "opacity-100" : "opacity-0"}`} />
                                    <span>Select Outbound Caller ID (Default)</span>
                                  </button>
                                )}
                                {activePhoneNumbers
                                  .filter((pn) => {
                                    if (!callerIdSearch) return true;
                                    const search = callerIdSearch.toLowerCase();
                                    const number = (pn.number || "").toLowerCase();
                                    const name = (pn.friendlyName || "").toLowerCase();
                                    return number.includes(search) || name.includes(search);
                                  })
                                  .map((pn) => (
                                    <button
                                      key={pn.id}
                                      className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-sm hover-elevate cursor-pointer text-left"
                                      onClick={() => { setSelectedCallerId(pn.id); setCallerIdOpen(false); setCallerIdSearch(""); }}
                                      data-testid={`caller-id-option-${pn.id}`}
                                    >
                                      <Check className={`w-3 h-3 shrink-0 ${selectedCallerId === pn.id ? "opacity-100" : "opacity-0"}`} />
                                      <span className="truncate">
                                        {pn.friendlyName ? `${pn.number} (${pn.friendlyName})` : pn.number}
                                      </span>
                                    </button>
                                  ))}
                                {customCallerIdsFeatureEnabled && (customCallerIdsList || []).length > 0 && (
                                  <>
                                    {activePhoneNumbers.length > 0 && <div className="my-1 border-t" />}
                                    <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Custom Caller IDs</div>
                                    {(customCallerIdsList || [])
                                      .filter((c) => {
                                        if (!callerIdSearch) return true;
                                        const search = callerIdSearch.toLowerCase();
                                        return (c.number || "").toLowerCase().includes(search) || (c.name || "").toLowerCase().includes(search);
                                      })
                                      .map((c) => {
                                        const val = `custom:${c.number}`;
                                        return (
                                          <button
                                            key={c.id}
                                            className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-sm hover-elevate cursor-pointer text-left"
                                            onClick={() => { setSelectedCallerId(val); setCallerIdOpen(false); setCallerIdSearch(""); }}
                                            data-testid={`caller-id-option-custom-${c.id}`}
                                          >
                                            <Check className={`w-3 h-3 shrink-0 ${selectedCallerId === val ? "opacity-100" : "opacity-0"}`} />
                                            <span className="truncate">{c.number} ({c.name})</span>
                                          </button>
                                        );
                                      })}
                                  </>
                                )}
                                {activePhoneNumbers.filter((pn) => {
                                  if (!callerIdSearch) return true;
                                  const search = callerIdSearch.toLowerCase();
                                  return (pn.number || "").toLowerCase().includes(search) || (pn.friendlyName || "").toLowerCase().includes(search);
                                }).length === 0 && !(customCallerIdsFeatureEnabled && (customCallerIdsList || []).some((c) => {
                                  if (!callerIdSearch) return true;
                                  const search = callerIdSearch.toLowerCase();
                                  return (c.number || "").toLowerCase().includes(search) || (c.name || "").toLowerCase().includes(search);
                                })) && callerIdSearch && (
                                  <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                                    No numbers matching "{callerIdSearch}"
                                  </div>
                                )}
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-1.5 mb-4">
                      {digits.map(({ digit, sub }) => (
                        <Button
                          key={digit}
                          variant="outline"
                          className="h-12 flex flex-col gap-0 rounded-md"
                          onClick={() => handleDigitPress(digit)}
                          data-testid={`button-digit-${digit}`}
                        >
                          <span className="text-lg font-medium leading-none">
                            {digit}
                          </span>
                        </Button>
                      ))}
                    </div>

                    {dncWarning && dialTab === "phone" && (
                      <div
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-destructive/10 text-destructive text-xs"
                        data-testid="text-dnc-warning"
                      >
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>
                          {dncWarning.contactName || "This contact"} is on the
                          Do Not{" "}
                          {dncWarning.type === "call"
                            ? "Call"
                            : dncWarning.type === "sms"
                              ? "SMS"
                              : "Email"}{" "}
                          list.
                        </span>
                      </div>
                    )}

                    <div className="flex justify-center">
                      <Button
                        size="icon"
                        className="w-14 h-14 rounded-full bg-green-600 text-white hover-elevate"
                        onClick={() => {
                          if (dialTab === "extension" && selectedExtension) {
                            const callerIdToUse = selectedCallerId !== "auto"
                              ? selectedCallerId.startsWith("custom:")
                                ? selectedCallerId.slice(7)
                                : activePhoneNumbers.find((pn) => pn.id === selectedCallerId)?.number
                              : undefined;
                            makeCall(selectedExtension, callerIdToUse);
                          } else {
                            handleCall();
                          }
                        }}
                        disabled={
                          !isRegistered ||
                          (dialTab === "phone" ? inputValue.length === 0 : !selectedExtension) ||
                          isDispositionBlocked
                        }
                        data-testid="button-make-call"
                      >
                        <Phone className="w-6 h-6" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showCsatPickerDialog} onOpenChange={(open) => { if (!open) setShowCsatPickerDialog(false); }}>
        <DialogContent className="sm:max-w-sm" data-testid="dialog-dialpad-csat-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SmilePlus className="h-4 w-4 text-blue-500" />
              Send CSAT Survey
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-muted-foreground">
              The caller will be transferred to the survey IVR. Override the queue default if they reached the wrong queue.
            </p>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Survey Template</label>
              <Select value={csatPickerTemplateId} onValueChange={setCsatPickerTemplateId}>
                <SelectTrigger data-testid="select-dialpad-csat-template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__" data-testid="option-dialpad-csat-auto">Auto (queue default)</SelectItem>
                  {csatTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id} data-testid={`option-dialpad-csat-${template.id}`}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {csatTemplatesLoading && (
                <p className="text-xs text-muted-foreground" role="status">Loading survey templates...</p>
              )}
              {csatTemplatesError && (
                <p className="text-xs text-destructive" role="alert">Unable to load survey templates. Auto selection is still available.</p>
              )}
              {!csatTemplatesLoading && !csatTemplatesError && csatTemplates.length === 0 && (
                <p className="text-xs text-muted-foreground">No active survey templates are available. Auto selection will be used.</p>
              )}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => setShowCsatPickerDialog(false)} data-testid="button-dialpad-csat-cancel">
              Cancel
            </Button>
            <Button
              disabled={csatTransferring}
              data-testid="button-dialpad-csat-confirm"
              onClick={async () => {
                setCsatTransferring(true);
                setShowCsatPickerDialog(false);
                try {
                  const templateId = csatPickerTemplateId === "__auto__" ? undefined : csatPickerTemplateId;
                  await transferToSurvey(templateId);
                  toast({ title: "Transferring caller to survey..." });
                } catch (e: any) {
                  toast({ title: "Survey transfer failed", description: e?.message || "Unknown error", variant: "destructive" });
                } finally {
                  setCsatTransferring(false);
                }
              }}
            >
              <SmilePlus className="h-3.5 w-3.5 mr-1.5" />
              {csatTransferring ? "Transferring..." : "Transfer to Survey"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Microphone blocked — shown when agent tries to answer/make a call with mic denied */}
      <Dialog open={micBlockedDialogOpen} onOpenChange={setMicBlockedDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-mic-blocked">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-destructive">🎤</span> Microphone Access Denied
            </DialogTitle>
            <DialogDescription className="pt-1">
              {micBlockedMode === "outbound"
                ? "Your outbound call could not be placed. You will not be able to make calls until microphone access is enabled."
                : "The call has been passed to the next available agent. You will not be able to receive calls until microphone access is enabled."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium">To enable your microphone:</p>
            <ol className="space-y-2 text-sm text-muted-foreground list-none">
              <li className="flex items-start gap-2">
                <span className="font-semibold text-foreground min-w-[18px]">1.</span>
                <span>Click the icon to the left of the address bar and select <strong className="text-foreground">View site information</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-foreground min-w-[18px]">2.</span>
                <span>Find <strong className="text-foreground">Microphone</strong> and switch it to <strong className="text-foreground">Allow</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold text-foreground min-w-[18px]">3.</span>
                <span>
                  {micBlockedMode === "outbound"
                    ? "Refresh the page — you will then be ready to make calls"
                    : "Refresh the page — you will then be ready to receive calls"}
                </span>
              </li>
            </ol>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setMicBlockedDialogOpen(false)}
              data-testid="button-mic-blocked-dismiss"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
